import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Strings from "../../utils/Strings";
import {
  config,
  DOBASEURL,
  errorNotify,
  getValueFromLocalStorage,
  localStorageKey,
  successNotify,
} from "../../utils/helper";
import { authorizedPost } from "../../Config/networkWithToken";
import configuration from "../../Config/configuration";
import Spinner from "../../Components/Spinner/Spinner";
import Images from "../../utils/images";
import { MdPersonOff } from "react-icons/md";
import { FaCheckCircle, FaHeart, FaRegHeart } from "react-icons/fa";
import { useFavourites, useToggleFavourite, getProfileId } from "../../Admin/Pages/Favourites/useFavourites";

const ProfileList = ({ userData, role, canSendRequest }) => {
  const [loading, setLoading] = useState(false);
  const [buttonStatus, setButtonStatus] = useState(false);
  const [open, setOpen] = useState(false);
  const [favouriteLoading, setFavouriteLoading] = useState(false);
  const userId = getValueFromLocalStorage(localStorageKey.USERDATA, "_id");
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const currentPage = Number(searchParams.get("page")) || 1;
  // Coordinators get the same view as admin/superAdmin (profile details, View
  // Profile button, unblurred photos) but are fully read-only -- no mutating
  // action, so anything that changes data must gate on canManage instead.
  const isAdminLike = role === "admin" || role === "superAdmin" || role === "coordinator";
  const canManage = role === "admin" || role === "superAdmin";
  const safeJoin = (val) => (Array.isArray(val) ? val.join(", ") : val || "");

  // Favourites are personal per-admin. Admin accounts store their own id
  // under "adminId" (not "_id", which is the end-user's own profile id used
  // elsewhere in this file), shared across every ProfileList card via the
  // react-query cache so this doesn't refetch per card.
  const adminId = getValueFromLocalStorage(localStorageKey.USERDATA, "adminId");
  const profileId = getProfileId(userData);
  const { data: favourites = [] } = useFavourites(adminId, {
    enabled: canManage,
  });
  const isFavourite = favourites.some((item) => getProfileId(item) === profileId);
  const toggleFavourite = useToggleFavourite(adminId);

  const handleToggleFavourite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!profileId || favouriteLoading) return;
    setFavouriteLoading(true);
    try {
      await toggleFavourite.mutateAsync({ profileId, isFavourite });
    } catch (error) {
      console.log(error);
      errorNotify("Could not update favourite. Please try again.");
    } finally {
      setFavouriteLoading(false);
    }
  };

  const expectationData = userData?.profile?.expectations;

  const expectationItems = expectationData
    ? [
        { label: "Age From", value: expectationData?.ageFrom },
        { label: "Age To", value: expectationData?.ageTo },
        { label: "Height From", value: expectationData?.heightFrom },
        { label: "Height To", value: expectationData?.heightTo },
        {
          label: "No Of HK Mantra",
          value: expectationData?.expetedCountOfMantra,
        },
        {
          label: "Regulative Principles",
          value: safeJoin(expectationData?.expectedRegulativePrinciples),
        },
        { label: "Caste", value: safeJoin(expectationData?.expetedCaste) },
        {
          label: "Sub Caste",
          value: safeJoin(expectationData?.expetedSubcaste),
        },
        {
          label: "Education",
          value: safeJoin(expectationData?.expetedEducation),
        },
        {
          label: "Employed In",
          value: safeJoin(expectationData?.expetedEmployedIn),
        },
        { label: "City", value: expectationData?.expectedCity },
        {
          label: "Country",
          value: safeJoin(expectationData?.expetedCountrysLiving),
        },
        {
          label: "Manglik",
          value: safeJoin(expectationData?.expectedManglick),
        },
        { label: "Religion", value: expectationData?.expetedReligion },
        {
          label: "Ethnicities",
          value: safeJoin(expectationData?.expetedEthnicities),
        },
        {
          label: "Marital Status",
          value: safeJoin(expectationData?.expetedMaritalStatus),
        },
        {
          label: "Mother Tongue",
          value: safeJoin(expectationData?.expetedMotherTongue),
        },
        {
          label: "Languages",
          value: safeJoin(expectationData?.expetedLanguagesCanSpeak),
        },
        {
          label: "Partner Description",
          value: safeJoin(expectationData?.expetedPartnerDescription),
        },
        {
          label: "Occupations",
          value: safeJoin(expectationData?.expetedOccupations),
        },
        {
          label: "Annual Income From",
          value: expectationData?.expetedIncomeFrom,
        },
        { label: "Annual Income To", value: expectationData?.expetedIncomeTo },
        {
          label: "Country Currency",
          value: safeJoin(expectationData?.countryCurrency),
        },
        {
          label: "Wants NRI",
          value: safeJoin(expectationData?.expetedWantNRI),
        },
        {
          label: "Spiritual Status",
          value: safeJoin(expectationData?.expetedSpiritualStatus),
        },
        {
          label: "Spiritual Master",
          value: safeJoin(expectationData?.expetedSpiritualMaster),
        },
      ].filter((item) => item.value)
    : [];

  const handleSendRequest = async () => {
    setLoading(true);
    try {
      const data = { userId: userData._id, requestedUserId: userId };
      const res = await authorizedPost(
        configuration.apis.sendRequest,
        data,
        config,
      );
      const response = res?.data;

      if (response && response.status) {
        successNotify(response.message);
        setButtonStatus(true);
      } else {
        errorNotify(response?.message || "Request failed unexpectedly.");
      }
    } catch (error) {
      console.log(error);
      errorNotify("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const activeStatus = () => {
    if (userData?.isNoResponse) return "No Response";
    else if (userData?.isNotInterested) return "Not Interested";
    else if (
      userData?.hasOwnProperty("isActive") &&
      userData?.isActive &&
      userData?.profileCompleted
    )
      return "Active";
    else if (
      !userData?.hasOwnProperty("isActive") &&
      userData?.profileCompleted
    )
      return "Active";
    else if (
      userData?.profileCompletionPercentage > 0 &&
      (!userData?.profileCompleted || !userData?.isActive)
    )
      return "Registered";
    else if (userData?.profileCompletionPercentage === 0) return "SignUp";
  };

  const statusColor = {
    Active: "bg-green-400",
    Registered: "bg-yellow-400",
    SignUp: "bg-red-400",
    "Not Interested": "bg-gray-400",
    "No Response": "bg-gray-400",
  };

  const currentStatus = activeStatus();
  const colorClass = statusColor[currentStatus] || "bg-yellow-400";

  return (
    <div className="relative mb-4 flex flex-col rounded-lg border border-gray-200 bg-bgWhite shadow-sm">
      {canManage && (
        <button
          type="button"
          onClick={handleToggleFavourite}
          disabled={favouriteLoading}
          aria-label={isFavourite ? "Remove from favourites" : "Add to favourites"}
          title={isFavourite ? "Remove from favourites" : "Add to favourites"}
          className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/90 shadow-md transition-transform hover:scale-110 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isFavourite ? (
            <FaHeart className="text-lg text-red-500" />
          ) : (
            <FaRegHeart className="text-lg text-gray-400" />
          )}
        </button>
      )}
      <div
        className={`flex flex-col gap-2 pb-2 pl-2 md:flex-row ${canManage ? "pt-12" : "pt-6"}`}
      >
        <div className="flex flex-col items-center md:w-[25%] xl:w-[15%]">
        <div className="relative flex w-full items-center justify-center md:h-[200px] xl:h-[120px]">
          <img
            src={
              userData?.photos?.[0]
                ? DOBASEURL + userData.photos[0]
                : userData?.images?.[0]
                  ? DOBASEURL + userData.images[0]
                  : userData?.gender === "male"
                    ? Images.MEN
                    : Images.WOMEN
            }
            alt=""
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            className={`mt-10 h-32 w-32 rounded-lg object-cover shadow-md ${
              isAdminLike || userData?.requestStatus === "Accepted"
                ? ""
                : "blur"
            }`}
          />
          {userData?.isMentorVerified && (
            <div className="absolute right-2 top-2 flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700 shadow-sm">
              <FaCheckCircle className="text-green-600" />
              <span>Verified</span>
            </div>
          )}
        </div>

        {/* NEW: Admin/Coordinator-only link to the full "Current Status"
            page (suggestions / coordinator suggestions / requests /
            questionnaires with partner names). */}
        {isAdminLike && (
          <Link
            to={`/matrimony/${currentPage}/user-status/${userData?._id || userData?.userId}`}
            className="mt-2 text-xs font-medium text-blue-600 underline hover:text-blue-800 xl:mt-8"
          >
           User Current Status - View More
          </Link>
        )}
        </div>

        {userData?.isCourseVerified && (
          <div className="absolute right-3 top-12 z-[5] hidden items-center gap-2 text-[12px] font-bold tracking-wide text-blue-600 md:flex">
            <span className="drop-shadow-sm">Course Attended</span>
          </div>
        )}

        <div className="flex w-full flex-col items-start justify-between gap-2 p-2 xl:flex-row">
          <div className="w-full text-black xl:w-[85%]">
            {role !== "user" && (
              <div className="flex w-full flex-col items-center justify-between gap-1 md:flex-row">
                <h2 className="font-jost text-lg font-semibold">
                  {userData.name}
                </h2>
                <div className="flex items-center gap-2 xl:hidden">
                  <div
                    className={`h-[10px] w-[10px] rounded-full ${colorClass}`}
                  ></div>
                  {currentStatus}
                  {(userData?.isActive ||
                    (!userData?.hasOwnProperty("isActive") &&
                      userData?.profileCompleted)) &&
                    !userData?.isRequested &&
                    !userData?.isMatched && (
                      <MdPersonOff className="text-3xl text-red-600" />
                    )}
                </div>
              </div>
            )}

            {/* 3 column grid */}
            <div className="font-jost mt-1 grid grid-cols-1 gap-1 md:grid-cols-3">
              {/* Column 1 */}
              <div className="flex flex-col gap-1">
                {userData?.isCourseVerified && (
                  <div className="flex items-center gap-1.5 py-1 text-[11px] font-bold text-blue-600 md:hidden">
                    Course Attended
                  </div>
                )}
                <div>
                 {Strings.DOB}:{" "}
                  <span className="text-textGreen">
                 {userData?.dob ? userData.dob.split("T")[0] : "NA"}
               </span>
               </div>
                <div>
                  {Strings.BIRTH_TIME}:{" "}
                  <span className="text-textGreen">
                    {userData?.birthTime || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.BIRTH_CITY}:{" "}
                  <span className="text-textGreen">
                    {userData?.birthCity || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.OCCUPATIONS}:{" "}
                  <span className="text-textGreen">
                    {userData?.occupations?.length
                      ? userData?.occupations?.[0]?.slice(0, 10) +
                        (userData?.occupations.join(", ").length > 10
                          ? "..."
                          : "")
                      : "NA"}
                  </span>
                </div>
                {isAdminLike && (
                  <div>
                    Signup Date:{" "}
                    <span className="text-textGreen">
                      {userData?.registerDate
                        ? new Date(userData.registerDate).toLocaleDateString(
                            "en-CA",
                          )
                        : "NA"}
                    </span>
                  </div>
                )}
              </div>

              {/* Column 2 */}
              <div className="flex flex-col gap-1">
                <div>
                  {Strings.HEIGHT}:{" "}
                  <span className="text-textGreen">
                    {userData?.height || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.MOTHERTONGUE}:{" "}
                  <span className="text-textGreen">
                    {userData?.motherTongue || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.EDUCATION}:{" "}
                  <span className="text-textGreen">
                    {userData?.education?.length
                      ? userData?.education?.[0]?.slice(0, 10) +
                        (userData?.education.join(", ").length > 10
                          ? "..."
                          : "")
                      : "NA"}
                  </span>
                </div>
                <div>
                  {Strings.Marital}:{" "}
                  <span className="text-textGreen">
                    {userData?.maritalStatus || "NA"}
                  </span>
                </div>
                {userData?.maritalStatus !== "never married" && (
                  <div>
                    {Strings.Children}:{" "}
                    <span className="text-textGreen">
                      {userData?.childrenCount || "0"}
                    </span>
                  </div>
                )}
                {isAdminLike && (
                  <div>
                    Registration Date:{" "}
                    <span className="text-textGreen">
                      {userData?.registerDate
                        ? new Date(userData.registerDate).toLocaleDateString(
                            "en-CA",
                          )
                        : "NA"}
                    </span>
                  </div>
                )}
              </div>

              {/* Column 3 */}
              <div className="flex flex-col gap-1">
                <div>
                  {Strings.CURRENT_CITY}:{" "}
                  <span className="text-textGreen">
                    {userData?.currentCity
                      ? userData.currentCity.length > 10
                        ? `${userData.currentCity.slice(0, 10)}...`
                        : userData.currentCity
                      : "NA"}
                  </span>
                </div>
                <div>
                  {Strings.ANNUAL_INCOME}:{" "}
                  <span className="text-textGreen">
                    {userData?.annualIncome || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.SPIRITUAL_STATUS}:{" "}
                  <span className="text-textGreen">
                    {userData?.spiritualStatus || "NA"}
                  </span>
                </div>
                <div>
                  {Strings.CASTE}:{" "}
                  <span className="text-textGreen">
                    {userData?.caste || "NA"}
                  </span>
                </div>
                {isAdminLike &&
                  userData?.profileCompletionPercentage >= 80 &&
                  userData?.profileCompletedDate && (
                    <div>
                      Profile Completed Date:{" "}
                      <span className="text-textGreen">
                        {new Date(
                          userData?.profileCompletedDate,
                        ).toLocaleDateString("en-CA") || "NA"}
                      </span>
                    </div>
                  )}
              </div>
            </div>
            {expectationItems.length > 0 && !open && (
              <div className="mt-2 flex w-full justify-center">
                <button
                  className="flex items-center gap-1 rounded-md border border-blue-400 px-3 py-1 text-sm font-medium text-blue-500 hover:bg-blue-50 hover:text-blue-700"
                  onClick={() => setOpen(true)}
                >
                  profile expectations
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>

          <div className="flex h-full flex-col items-center justify-center gap-2 xl:w-auto">
            {isAdminLike && (
              <div className="flex h-full flex-col">
                <div className="hidden items-center justify-end gap-2 xl:flex">
                  <div
                    className={`h-[10px] w-[10px] rounded-full ${colorClass}`}
                  ></div>
                  {currentStatus}
                  {(((userData?.isActive ||
                    (!userData?.hasOwnProperty("isActive") &&
                      userData?.profileCompleted)) &&
                    !userData?.isRequested &&
                    !userData?.isMatched) ||
                    (userData?.profileCompletionPercentage > 0 &&
                      (!userData?.profileCompleted || !userData?.isActive) &&
                      !userData?.isRequested &&
                      !userData?.isMatched)) && (
                    <MdPersonOff className="text-3xl text-red-600" />
                  )}
                </div>
                <div className="flex flex-1 items-center justify-center">
                  <Link
                    to={`/matrimony/${currentPage}/viewProfile/${userData?._id || userData?.userId}`}
                    className="w-full"
                  >
                    <button className="font-jost w-full rounded-lg bg-textYellow px-2 py-1 text-xs text-white md:w-auto md:px-4 md:py-2 lg:text-sm">
                      {Strings.VIEW_PROFILE}
                    </button>
                  </Link>
                </div>
              </div>
            )}

            {role === "user" && canSendRequest && (
              <div className="w-full justify-center md:flex">
                {buttonStatus ? (
                  <button className="w-full rounded-lg bg-textYellow px-2 py-1 text-sm text-white md:w-auto md:px-6 md:py-2">
                    Pending
                  </button>
                ) : (
                  <button
                    className="w-full rounded-lg bg-textYellow px-2 py-1 text-sm text-white md:w-auto md:px-6 md:py-2"
                    onClick={handleSendRequest}
                    disabled={loading}
                  >
                    {loading ? <Spinner className="mx-auto" /> : "Send"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {open && expectationItems.length > 0 && (
        <div className="px-4 pb-3 pt-1">
          <div className="mb-2 flex items-center">
            <span className="mt-9 text-1xl font-semibold text-textYellow">
               Profile Expectations
            </span>
          </div>
          <div className="grid grid-cols-1 gap-1 md:grid-cols-3">
            {expectationItems.map((item, idx) => (
              <div key={idx}>
                {item.label}:{" "}
                <span className="text-textGreen">{item.value}</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex justify-center">
            <button
              className="flex items-center gap-1 rounded-md border border-blue-400 px-3 py-1 text-sm font-medium text-blue-500 hover:bg-blue-50 hover:text-blue-700"
              onClick={() => setOpen(false)}
            >
              View Less
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 15l7-7 7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileList;